import useProfile from "@/hooks/use-profile";
import useApi from "@/hooks/useApi";
import useStepper from "@/hooks/useStepper";
import LandingPageOptionRepository from "@/utilities/repositories/LandingPageOptions";
import LandingPagesRepository from "@/utilities/repositories/LandingPages";
import UserRepository from "@/utilities/repositories/Users";
import {
  E_LANDING_PAGE_TYPE,
  ILandingPage,
  ILandingPageDataForm,
} from "@/utilities/types/LandingPage";
import { IResponseData } from "@/utilities/types/requests";
import { Roles } from "@/utilities/types/Users";
import cn from "@/utils/class-names";
import * as React from "react";
import { FormProvider, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Stepper, Tooltip } from "rizzui";
import DomainCustomization from "./create-update/DomainCustomization";
import SelectProjectLDP from "./create-update/SelectProjectLDP";
import TemplateSelection from "./create-update/TemplateSections";
import ProjectRepository from "@/utilities/repositories/Project";
import { routes } from "@/config/routes";
import { useMemo } from "react";
import DnsDomainConfigurationGuide from "./create-update/DnsDomainConfigurationGuide";
import { useDnsGuideModal } from "./create-update/hooks/useDnsGuideModal";

export interface CreateLDPStateLocation {
  userId?: string;
  shouldToastError?: boolean;
  message?: string;
  hideBack?: boolean;
}
export interface CreateLDPWithStepperProps {
  backTo?: string;
}

export default function CreateLDPWithStepper({
  backTo,
}: CreateLDPWithStepperProps) {
  const navigate = useNavigate();
  const location = useLocation();

  const locationState: CreateLDPStateLocation = location.state;
  const form = useForm();
  const formValues = form.watch();
  const { profile, setProfile } = useProfile();

  const {
    isOpen: isDnsGuideOpen,
    guideData: customDomainData,
    handleDomainSubmit,
    handleDone: handleDnsGuideClose,
    handleClose,
    handleOpen,
  } = useDnsGuideModal({
    afterClose: () => backTo && navigate(backTo),
  });

  const validateStep = (step: number) => {
    // Validate based on current step and form values

    if (step === 0 && !formValues.project) {
      return false;
    }

    if (step === 1 && !formValues.option) {
      return false;
    }

    if (step === 2 && !formValues.domain) {
      return false;
    }

    return true;
  };

  const {
    currentStep,
    isLastStep,
    isFirstStep,
    canGoNext,
    goToNext,
    goToPrevious,
    onValidateStep,
    errorsStep,
  } = useStepper({
    steps: 3,
    validateStep,
  });
  const isAdmin = profile?.role === Roles.ADMIN;
  const userId = locationState?.userId || (!isAdmin ? profile?.id : "");

  const { request: createLandingPage, response: responseCreateLandingPage } =
    useApi<IResponseData<ILandingPage>>({
      request: LandingPagesRepository.createLandingPage,
      enableToast: true,
    });

  const { request: updateLandingPage } = useApi<IResponseData<ILandingPage>>({
    request: LandingPagesRepository.updateLandingPage,
    enableToast: true,
  });

  const afterSuccessfullyCreated = () => {
    backTo && navigate(backTo);
  };

  const onRemoveDomain = async (id: string) => {
    try {
      handleClose();
      //remove domain
      const result = await updateLandingPage(id, {
        type: E_LANDING_PAGE_TYPE.SUBDOMAIN,
      });
      if (result?.data) {
        navigate(routes.landingPages.listing, {
          state: { userProjectLandingPage: result.data },
        });
      } else {
        handleOpen();
      }
    } catch (error) {}
  };

  const handleAttachOption = async (optionId: string, ldpId: string) => {
    try {
      await LandingPageOptionRepository.attachOption({
        landingPageId: ldpId,
        layoutOptionId: optionId,
      });
      await LandingPageOptionRepository.activeOption({
        landingPageId: ldpId,
        layoutOptionId: optionId,
        active: true,
      });
    } catch (error: unknown) {
      const errorWithMessage = error as { message?: string };
      toast.error(errorWithMessage?.message ?? "Something went wrong");
    }
  };

  const onCreateLDP = async ({
    domainName,
    domain,
    type,
    cNameTargetDomain,
    cNameTarget,
  }: {
    domainName: string;
    domain: string;
    type: E_LANDING_PAGE_TYPE;
    cNameTargetDomain?: string;
    cNameTarget?: string;
  }) => {
    if (
      !formValues?.project?.isProjectExist &&
      profile?.role === Roles.AGENCY
    ) {
      // Add the project in the user's inventory project list if not exist
      await ProjectRepository.addUserProject(formValues?.project?.id);
    }

    const payload: ILandingPageDataForm = {
      name: domainName,
      projectId: formValues?.project?.id || "",
      userDomainId: userId || "",
      type,
    };

    if (type === E_LANDING_PAGE_TYPE.SUBDOMAIN) {
      payload.subDomain = domain;
    } else {
      payload.customDomain = domain;
      payload.cNameTargetDomain = cNameTargetDomain || "";
      payload.cNameTarget = cNameTarget || "";
    }

    await createLandingPage(payload)
      .then(async (res) => {
        await handleAttachOption(
          formValues?.option?.id || "",
          res?.data?.id || ""
        );

        if (!profile?.isFlowSignUp) {
          await UserRepository.updateMe({
            isFlowSignUp: true,
          });
          if (profile && profile.role === Roles.AGENCY) {
            setProfile({
              ...profile,
              isFlowSignUp: true,
            });
          }
        }

        // Check if DNS Guide needs to be shown
        const showingDnsGuide = handleDomainSubmit(
          domain,
          type,
          cNameTargetDomain,
          undefined,
          cNameTarget
        );

        // If DNS Guide is not shown (subdomain), perform redirect
        if (!showingDnsGuide) {
          afterSuccessfullyCreated();
        }
      })
      .catch((err: unknown) => {
        const errorWithMessage = err as { message?: string };
        console.error(errorWithMessage?.message || "Something went wrong");
      });
  };

  const onNext = async () => {
    if (!canGoNext) {
      if (currentStep === 0) {
        toast.error("Please select a project");
      } else if (currentStep === 1) {
        toast.error("Please select an option");
      } else if (currentStep === 2) {
        toast.error("Please select a domain");
      } else {
        toast.error("Please fill all the required fields");
      }
      return;
    }

    if (!isLastStep) {
      goToNext();
    }
  };

  const onPrev = () => {
    if (isFirstStep && backTo) {
      return navigate(backTo);
    }
    goToPrevious();
  };

  React.useEffect(() => {
    if (!locationState?.userId) {
      // toast.error("Please select a user to create landing page");
      setTimeout(() => {
        navigate(routes.landingPages.create);
      }, 500);
      return;
    }
    if (locationState?.shouldToastError) {
      toast.error(locationState?.message || "Something went wrong");
    }
  }, [
    locationState?.shouldToastError,
    locationState?.message,
    locationState?.userId,
    navigate,
  ]);

  const invalidStep = useMemo(() => {
    return !!errorsStep?.[currentStep];
  }, [errorsStep, currentStep]);

  return (
    <FormProvider {...form}>
      <div className="w-full relative flex flex-1 flex-col mt-8 min-h-[calc(100svh-32px)]">
        <div className="  px-4 max-w-screen-xl w-full mx-auto">
          {/* <div className="mb-4">
            <Progressbar
              value={progress}
              color="primary"
              size="sm"
              label={`Step ${currentStep + 1} of ${totalSteps}`}
            />
          </div> */}
          <Stepper className="w-full" currentIndex={currentStep}>
            <Stepper.Step
              title="Project"
              description={
                errorsStep?.[0] ||
                form.getValues("project")?.name ||
                "Pick your Project"
              }
              status={invalidStep ? "error" : undefined}
              color={invalidStep ? "danger" : undefined}
            />
            <Stepper.Step
              title="Option"
              description={form.getValues("option")?.name || "Select an Option"}
            />
            <Stepper.Step title="Domain" description="Configure your domain" />
          </Stepper>
        </div>

        <div className="max-w-screen-xl mx-auto w-full flex-1 h-auto overflow-auto">
          {currentStep === 0 && (
            <SelectProjectLDP
              onValidateStep={(errorMessage) => {
                onValidateStep?.(0, errorMessage);
              }}
              userId={userId}
              invalidStep={invalidStep}
            />
          )}
          {currentStep === 1 && (
            <TemplateSelection
              onSelectTemplate={(template) => form.setValue("option", template)}
              activeOptionId={formValues?.option?.id || ""}
            />
          )}
          {currentStep === 2 && (
            <DomainCustomization
              projectId={formValues?.project?.id || ""}
              domainInitialValues={formValues.domain}
              onSave={async (values) => {
                await onCreateLDP({
                  domainName: values?.domainName || "",
                  domain: values?.domain || "",
                  type: values?.type || E_LANDING_PAGE_TYPE.SUBDOMAIN,
                  cNameTargetDomain: values?.cNameTargetDomain || "",
                  cNameTarget: values?.cNameTarget || "",
                });
              }}
            />
          )}
        </div>

        <div className="shadow-xl border-t border-gray-200 bg-white -mb-8 p-8 sticky bottom-0 left-0">
          <div className="max-w-screen-xl mx-auto w-full flex justify-between gap-5">
            <Button
              className={cn(
                "",
                isFirstStep &&
                  (!backTo || locationState?.hideBack) &&
                  "invisible"
              )}
              onClick={onPrev}
            >
              {isFirstStep && backTo ? "Back" : "Previous Step"}
            </Button>

            {!isLastStep ? (
              <Tooltip
                placement="left"
                content={errorsStep?.[currentStep] || null}
                className={cn(!invalidStep && "hidden")}
              >
                <Button disabled={!canGoNext} onClick={onNext}>
                  Next Step
                </Button>
              </Tooltip>
            ) : (
              <div /> // Empty div to maintain layout when last step
            )}
          </div>
        </div>

        {isDnsGuideOpen && customDomainData && (
          <DnsDomainConfigurationGuide
            isOpen={isDnsGuideOpen}
            onDone={handleDnsGuideClose}
            customDomain={customDomainData.customDomain}
            cNameTargetDomain={customDomainData.cNameTargetDomain}
            cNameTarget={customDomainData.cNameTarget}
            onRemove={onRemoveDomain}
            id={responseCreateLandingPage?.data?.id}
          />
        )}
      </div>
    </FormProvider>
  );
}
